import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { InfiniteData } from '@tanstack/query-core';
import { useQueryClient } from '@tanstack/react-query';

import NotificationKindChip from 'components/notifications/NotificationKindChip';
import { NotificationState } from 'components/shared/Notification';
import { Flags } from 'constants/flags';
import { useProfile } from 'context/profile';
import { useDebouncedWebsocketSubscription } from 'hooks/useDebouncedWebsocketSubscription';
import Notification from 'models/Notification';
import { notificationQueryKeys, NotificationsPage, useUpdateNotification } from 'services/api/notification';
import { useNotificationActions } from 'stores/notificationStore';

// The cable frame is a content-free refetch nudge (In-App Notifications tech design §4.6): it
// carries no payload BY DESIGN (the PHI boundary), so it is only ever a signal to refetch. Toast
// content comes from the refetch diff, never the frame.
type NotificationChannelMessage = { action: 'refetch' };

// Collapse a burst of nudges (a review with attachments fires several types at once; the
// local-midnight cron dispatch) into a single refetch — matches the chat/referral subscribers.
const NUDGE_DEBOUNCE_MS = 300;

// New-notification toasts self-dismiss after 8s via the provider's animated slide-out (Taylor).
const NOTIFICATION_TOAST_TTL_MS = 8000;

type NotificationsFeedData = InfiniteData<NotificationsPage, string | undefined>;

/**
 * Live badge/inbox/toast updates for In-App Notifications (OLIO-3060, story 7). Subscribes the
 * per-user `NotificationChannel` (identity is the authorization — no params) through the debounced
 * websocket hook, gated on the `in_app_notifications` flag so an un-flagged user attempts no
 * subscription at all. On a nudge it invalidates the shared `notifications/index` prefix — one
 * invalidation covers the badge and both tabs, since the unread count is a `select` off the index
 * meta (there is no separate count key). Toasts come from the refetch diff: each fresh unread row
 * not seen before becomes one toast on the AI-job slide-in stack.
 *
 * Mounted once, alongside `useEscalationWebSocket`, in the persistent `UserBar`.
 */
export function useNotificationsWebSocket() {
  const { profile } = useProfile();
  const enabled = profile.hasFlag(Flags.InAppNotifications);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addNotification } = useNotificationActions();
  const updateNotification = useUpdateNotification();

  // The first connect only confirms the stream — the feed already fetched on mount, so there is
  // nothing to resync. A LATER connect is a reconnect after a cable drop; refetching then
  // converges anything missed while offline. NotificationChannel transmits nothing on subscribe,
  // so there is no subscribe-time frame to skip (unlike WorkflowChannel/ChatConversationChannel) —
  // the reconnect handshake is the only resync trigger, and every received frame is a real nudge.
  const everConnected = useRef(false);

  // Every notification id we have already accounted for (toasted, or present at baseline / after
  // any refetch). Diffing against this persistent set — not a re-snapshot of the live cache per
  // call — is what makes overlapping refetches safe: two nudges whose refetches settle together
  // can't double-toast the same row, and the trailing guard below guarantees a row that landed
  // mid-refetch still gets picked up. `null` until the first refetch seeds the baseline.
  const seenIdsRef = useRef<Set<string> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);

  // Open a toast's target with the panel row's one-action semantic, mirrored from
  // NotificationInbox.handleOpen: mark read if unread, then SPA-navigate. The toast itself slides
  // out via the provider's animated dismiss (wired to the action click there).
  const handleToastClick = useCallback(
    (notification: Notification) => {
      if (notification.isUnread) updateNotification.mutate({ id: notification.id, read: true });
      const { targetUrl } = notification.notificationEvent;
      if (targetUrl) navigate(targetUrl);
    },
    [navigate, updateNotification]
  );

  const addToast = useCallback(
    (notification: Notification) => {
      addNotification({
        // The Notification row's own uuid — deterministic, and unique by the same unique
        // index that wrote the row (the seen-set already prevents double-toasting it).
        id: notification.id,
        state: NotificationState.NOTIFICATION,
        title: notification.notificationEvent.message,
        // The inbox row's kind chip, 1:1 (Taylor, story-7 review).
        icon: <NotificationKindChip kind={notification.notificationEvent.kind} />,
        // The row's link convention — patient name, "View" fallback (never the AI-job default).
        actionSuccessText: notification.notificationEvent.linkLabel,
        onActionClick: () => handleToastClick(notification),
        onDismiss: () => {},
        autoDismissMs: NOTIFICATION_TOAST_TTL_MS,
      });
    },
    [addNotification, handleToastClick]
  );

  // Refetch the feed (badge + both tabs, via the shared index prefix) and toast every genuinely
  // new unread row. The first settle seeds the baseline silently (an initial page load is never
  // toasted); thereafter each fresh first-page unread row not already seen becomes a toast. A
  // nudge arriving mid-refetch defers to a trailing refetch so its row is never dropped.
  const refetchAndToast = useCallback(() => {
    if (inFlightRef.current) {
      pendingRef.current = true;
      return;
    }
    inFlightRef.current = true;

    // Seed from the pre-refetch cache the first time, so the rows already on screen at mount are
    // the baseline (not toasts) while rows arriving in THIS refetch still surface.
    if (seenIdsRef.current === null) {
      const before = queryClient.getQueryData<NotificationsFeedData>(notificationQueryKeys.index({}));
      seenIdsRef.current = new Set(before?.pages.flatMap((page) => page.data.map((row) => row.id)) ?? []);
    }

    queryClient
      .invalidateQueries({ queryKey: notificationQueryKeys.index() })
      .then(() => {
        const seen = seenIdsRef.current!;
        const after = queryClient.getQueryData<NotificationsFeedData>(notificationQueryKeys.index({}));
        const firstPage = after?.pages[0]?.data ?? [];
        const newUnread = firstPage.filter((row) => row.isUnread && !seen.has(row.id));

        // Mark everything currently known (read or unread, all pages) seen so it never re-toasts.
        after?.pages.forEach((page) => page.data.forEach((row) => seen.add(row.id)));

        // Oldest first so the newest toast lands on top of the stack (the store prepends).
        newUnread.reverse().forEach((row) => addToast(row));
      })
      .finally(() => {
        inFlightRef.current = false;
        // A nudge landed while this refetch was in flight — refetch once more to catch its row.
        if (pendingRef.current) {
          pendingRef.current = false;
          refetchAndToast();
        }
      });
  }, [queryClient, addToast]);

  useDebouncedWebsocketSubscription<NotificationChannelMessage>({
    channel: 'NotificationChannel',
    debounceMs: NUDGE_DEBOUNCE_MS,
    enabled,
    onConnected: () => {
      if (!everConnected.current) {
        everConnected.current = true;
        return;
      }
      refetchAndToast();
    },
    onReceived: () => refetchAndToast(),
  });
}
